import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

interface Props {
  jobId: number;
  /**
   * PERSISTENT: this job has an applied search spec. Derived from
   * `specVersion != null`, never from `tuned` — see the call sites.
   */
  tuned: boolean;
  termCounts: { mustHave: number; exclusions: number };
  /**
   * When the applied spec took effect (`job_search_specs.created_at`). Null
   * means the response carried no date, and the banner then says NOTHING about
   * timing — US-510. It must never substitute a word like "recently" for a
   * fact it does not have.
   */
  tunedAt: string | null;
  /**
   * Whether THIS response's ordering actually came from the criteria
   * (`tuned` on the wire). False on budget exhaustion, the 15s ranking
   * deadline, or the route's catch-all — in which case the list is plain
   * cosine order and carries no chips, and saying otherwise would be a
   * misattribution the recruiter cannot detect.
   */
  rankingApplied: boolean;
}

function relativeDays(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

/**
 * US-404. Sits at the top of the suggested-candidates list — where a recruiter
 * scrolling a mediocre list already feels the problem — rather than beside
 * job-settings toggles, which would frame calibration as configuration to
 * understand before use. Copy leads with the payoff, never the noun
 * "Sourcing Agent" (spec §7).
 *
 * US-404 fix round: the tuned state deliberately has NO further call to
 * action ("Rate more →" was removed). That link routed back through
 * SourcingTuneEntry, which 409s on an existing agent, resolves the SAME
 * already-fully-judged round via current-round's highest-roundNumber lookup,
 * and lands the recruiter on a stale reveal/refinement screen for a round
 * that's already resolved — nothing on that path enqueues a NEW calibration
 * round. Round-2+ triggering is rejection-rate-triggered top-up rounds, a
 * deliberately deferred future phase (see process-calibration-round.ts) —
 * not something to fake here with a CTA that can't do anything.
 */
export function TuningBanner({ jobId, tuned, termCounts, tunedAt, rankingApplied }: Props) {
  if (!tuned) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 px-4 py-3 text-sm">
        <Sparkles className="h-4 w-4 shrink-0 text-blue-500" />
        <span className="text-gray-700 dark:text-gray-300">
          These suggestions are based on the job description alone. Want better ones? Rate 12 candidates and I'll tune what I look for.
        </span>
        <Link
          to={`/jobs/${jobId}/sourcing/tune`}
          className="ml-auto rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
        >
          Tune these suggestions
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-2.5 text-xs">
      <Sparkles className="h-3.5 w-3.5 shrink-0 text-blue-500" />
      {/* US-510. Left clause = the SPEC, a persistent fact. The old copy said
          "Tuned from your ratings recently": "recently" was fabricated (no
          timestamp was ever passed), and "from your ratings" is false for the
          v1 seed spec, which is created from the job's screening criteria at
          agent-creation time before a recruiter has rated anything. Naming the
          search criteria names the thing that actually exists. */}
      <span className="text-gray-600 dark:text-gray-400">
        Your search criteria{tunedAt ? `, set ${relativeDays(tunedAt)}` : ''} ·{' '}
        <span className="font-medium">{termCounts.mustHave} must-have{termCounts.mustHave === 1 ? '' : 's'}</span>,{' '}
        <span className="font-medium">{termCounts.exclusions} exclusion{termCounts.exclusions === 1 ? '' : 's'}</span>
      </span>
      {/* Right clause = THIS list. Separated from the left because they are
          separate facts that disagree whenever ranking degraded. "never remove
          anyone" attributes the no-filtering promise to the criteria — the Min
          match control directly below this banner on SourcingPage genuinely
          does filter, and the old wording read as denying that. */}
      <span className="text-gray-400 dark:text-gray-500 ml-auto">
        {rankingApplied
          ? 'Used to reorder this list — the criteria never remove anyone.'
          : 'Not applied to this list — showing plain match order.'}
      </span>
    </div>
  );
}
